import type { AdminAgentComposerProps } from "../models/admin-agent.models";
import { ADMIN_AGENT_MESSAGE_MAX_LENGTH, ADMIN_AGENT_RESUMABLE_STATUSES } from "../utils/admin-agent.constants";
import type { FormEvent, KeyboardEvent } from "react";

// Sending begins a backend run; a browser reconnect observes it rather than starting another copy.
const AdminAgentComposer = ({
  conversation,
  draft,
  pendingAction,
  onDraftChange,
  onSend,
  onStop,
  onResume,
}: AdminAgentComposerProps) => {
  const running = conversation?.status === "running";
  const busy = Boolean(pendingAction);
  const canSend = Boolean(conversation && draft.trim() && !running && !busy);
  const canResume = Boolean(
    conversation && !conversation.pendingApproval && ADMIN_AGENT_RESUMABLE_STATUSES.includes(conversation.status),
  );

  const sendTask = (event: FormEvent) => {
    event.preventDefault();

    if (canSend) {
      void onSend();
    }
  };

  const sendWithKeyboard = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();

      if (canSend) {
        void onSend();
      }
    }
  };

  return (
    <form
      onSubmit={sendTask}
      data-test="admin-agent-composer"
      className="border-greyscale-20 flex shrink-0 items-end gap-2 border-t p-2 dark:border-gray-700"
    >
      <label htmlFor="admin-agent-task" className="sr-only">
        {running ? "Task is running" : "Task or follow-up"}
      </label>
      <textarea
        id="admin-agent-task"
        data-test="admin-agent-message-input"
        value={draft}
        rows={2}
        maxLength={ADMIN_AGENT_MESSAGE_MAX_LENGTH}
        disabled={!conversation || pendingAction === "delete"}
        onChange={(event) => onDraftChange(event.target.value)}
        onKeyDown={sendWithKeyboard}
        aria-describedby="admin-agent-composer-help"
        placeholder={conversation ? "Ask the admin assistant…" : "Create or select a chat first"}
        title="Enter to send · Shift+Enter for a new line"
        className="block max-h-36 min-h-[56px] min-w-0 flex-1 resize-y rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm leading-5 outline-none focus:border-green focus:ring-1 focus:ring-green disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800"
      />
      <p id="admin-agent-composer-help" className="sr-only">
        {running ? "Stop to send a new instruction." : "Enter to send · Shift+Enter for a new line"}
      </p>
      <div className="flex shrink-0 flex-col gap-1">
        {(running || conversation?.pendingApproval) && (
          <button
            type="button"
            data-test="admin-agent-stop"
            disabled={busy}
            onClick={() => void onStop()}
            className="rounded border border-red-300 px-2 py-1.5 text-xs font-semibold text-red-700 disabled:opacity-50 dark:border-red-700 dark:text-red-300"
          >
            {pendingAction === "stop" ? "Stopping…" : "Stop"}
          </button>
        )}
        {canResume && (
          <button
            type="button"
            data-test="admin-agent-resume"
            disabled={busy}
            onClick={() => void onResume()}
            className="rounded border border-green px-2 py-1.5 text-xs font-semibold text-green disabled:opacity-50 dark:text-emerald-300"
          >
            {pendingAction === "resume" ? "Resuming…" : "Resume"}
          </button>
        )}
        {!running && (
          <button
            type="submit"
            data-test="admin-agent-send"
            disabled={!canSend}
            className="rounded bg-green px-2 py-1.5 text-xs font-semibold text-white hover:bg-[#166b60] disabled:opacity-50"
          >
            {pendingAction === "send" ? "Sending…" : "Send"}
          </button>
        )}
      </div>
    </form>
  );
};

export default AdminAgentComposer;
